// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import React from "react";
import { connect } from "react-redux";
import {
    FontIcon,
    CommandBarButton,
    IButtonStyles,
    IIconProps,
    IOverflowSetItemProps,
    OverflowSet,
    Customizer,
    ICustomizations,
    Separator,
    ContextualMenuItemType,
    Link,
    ActionButton,
    IconButton,
    Label,
    CommandButton,
    PrimaryButton,
    Checkbox,
} from "@fluentui/react";
import { IAdHocProject, IApplicationState, IProject } from "../../../models/applicationState";
import { PlatformType, isElectron } from "../../../common/hostProcess";
import { getLightGreyTheme, getSubMenuTheme } from "../../../common/themes";
import "./titleBar.scss";
import FileUploaderDialog from "../common/fileUploaderDialog/fileUploaderDialog";
import { getCookieName } from "../common/jsonSchemaFormHelper";
import { History } from "history";
import { NavLink } from "react-router-dom";
import Tooltip from 'react-tooltip'
import ReactTooltip from "react-tooltip";

const isElectronCheck = isElectron();
const platform =
    global && global.process && global.process.platform
        ? global.process.platform
        : PlatformType.Web;
const isMac = platform === "darwin";
const isLinux = platform === "linux";
const isElectronAndNotMacOrLinux = isElectronCheck && !(isMac || isLinux);
const isNotElectronAndMacOrLinux = !(isElectronCheck && (isMac || isLinux));

export interface ITitleBarProps extends React.Props<TitleBar> {
    icon?: string | JSX.Element;
    title?: string;
    project?: IProject;
    history?: History;
    onUploadComplete?: () => void;
    widgetMode?: boolean;
    adHocOcr?: IAdHocProject;
    postAdHocToSource?: () => void;
    canGenerateModel: boolean;
    onGenerateModelChange: (ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => void;
    runGenerateModel: boolean;
    hasExistingModel: boolean;
    adHocOcrV2: IAdHocProject;
}

export interface ITitleBarState {
    maximized: boolean;
    fullscreen: boolean;
    menu: Electron.Menu;
    showFileUpload: boolean;    
}

function mapStateToProps(state: IApplicationState) {
    return {
        title: state.appTitle,
    };
}

@connect(mapStateToProps, null)
export class TitleBar extends React.Component<ITitleBarProps, ITitleBarState> {
    public state: ITitleBarState = {
        maximized: false,
        fullscreen: false,
        menu: null,
        showFileUpload: false
    };

    private remote: Electron.Remote;
    private currentWindow: Electron.BrowserWindow;

    public componentDidMount() {
        if (isElectronCheck) {
            this.remote = window.require("electron").remote as Electron.Remote;
            this.currentWindow = this.remote.getCurrentWindow();
            this.currentWindow.on("maximize", () => this.onMaximize(true));
            this.currentWindow.on("unmaximize", () => this.onMaximize(false));
            this.currentWindow.on("enter-full-screen", () =>
                this.onFullScreen(true)
            );
            this.currentWindow.on("leave-full-screen", () =>
                this.onFullScreen(false)
            );

            this.setState({
                maximized: this.currentWindow.isMaximized(),
                fullscreen: this.currentWindow.isFullScreen(),
                menu: this.remote.Menu.getApplicationMenu(),
            });
        }
    }

    capitalizeFirstLetter(str) {
        return str.charAt(0).toUpperCase() + str.slice(1);
    }

    public render() {
        if (this.state.fullscreen) {
            return null;
        }

        const titleBarTheme: ICustomizations = {
            settings: {
                theme: getLightGreyTheme(),
            },
            scopedSettings: {},
        };

        const onRenderItem = (item: IOverflowSetItemProps): JSX.Element => {
            const buttonStyles: Partial<IButtonStyles> = {
                root: {
                    padding: "0 5px",
                    alignSelf: "stretch",
                },
            };
            const iconStyles: Partial<IIconProps> = {
                style: { display: "none" },
            };

            return (
                <CommandBarButton
                    menuProps={item.subMenuProps}
                    styles={buttonStyles}
                    text={item.name}
                    menuIconProps={iconStyles}
                />
            );
        };

        const onRenderOverflowButton = (
            overflowItems: any[] | undefined
        ): JSX.Element => {
            return (
                <CommandBarButton
                    ariaLabel="More items"
                    role="menuitem"
                    menuProps={{ items: overflowItems! }}
                />
            );
        };

        const onEditClick = () => {
            this.props.history.push(
                `/projects/${this.props.project.id}settings`
            );
        };

        const name = getCookieName();

        const showGenerateModel = this.props.adHocOcrV2 || this.props.canGenerateModel;
        let fontLabel = '';
        if (this.props.adHocOcr) {
            fontLabel = `Post to ${this.capitalizeFirstLetter(this.props.adHocOcr.source)}`;
        }
        if (this.props.adHocOcrV2) {
            fontLabel = `Post to ${this.capitalizeFirstLetter(this.props.adHocOcrV2.source)}`;
        }

        return (
            // hachet
            <div className="title-bar">
                <style>
                    {`
                        .checkbox-63 {
                            border: 1px solid #fff !important;
                        }              
                        .checkbox-63:hover {
                            border: 1px solid #ccc !important;
                        }              
                        .text-65 {
                            color: #fff;
                        }   
                        .root-60:hover .ms-Checkbox-text {
                            color: #ccc !important;
                        }    
                        .run-generate-model-cb-parent {
                            padding-top: 7px;
                            padding-right: 15px;                            
                        }    
                        .run-generate-model-cb-parent:hover .ms-Checkbox-text {
                            color: #ccc !important;
                        }
                        .root-84:hover .ms-Checkbox-text {
                            color: #fff;
                        }
                        .is-checked i {
                            color: #fff;
                        }
                        .ms-Checkbox-checkbox {
                            border-color: #fff !important;
                        }
                        .text-58 {
                            color: #fff;
                        }
                        .text-57 {
                            color: #fff;
                        }
                    `}
                </style>
                {/*<button onClick={() => this.props.onUploadComplete()}>*/}
                {/*    Upload Complete*/}
                {/*</button>*/}
                {isNotElectronAndMacOrLinux && (
                    <div className="title-bar-icon">
                        <FontIcon iconName="TagGroup" />
                    </div>
                )}
                {this.props.widgetMode && showGenerateModel &&
                    <React.Fragment>                        
                        <PrimaryButton ariaDescription={fontLabel} onClick={this.props.postAdHocToSource} className="save-to-optimus" >
                            <FontIcon 
                                    iconName='Save'                                     
                                    title={fontLabel}
                            />                        
                            &nbsp;
                            {fontLabel}
                        </PrimaryButton>
                        <div className="title-bar-icon"></div>
                        <div className="title-bar-main">
                            {this.props.adHocOcr && (
                                <Label className="adhoc-project-name">Document Service Id: {this.props.adHocOcr.project.name}</Label>                            
                            )}
                        </div>
                        <div className="title-bar-controls">
                            {/* <FontIcon
                                className="app-close-icon pull-right"
                                iconName="Cancel"
                                onClick={this.closeWindow}
                            /> */}
                            { showGenerateModel &&                                 
                                <div className="generate-model-checkbox" data-tip="React-tooltip">
                                    <Checkbox 
                                        label="Generate Model" 
                                        defaultChecked={this.props.runGenerateModel || (!!this.props.adHocOcrV2 && !this.props.adHocOcrV2.hasModel && !this.props.adHocOcrV2.hasRunningRequest)} 
                                        //checked={this.props.runGenerateModel || !this.props.adHocOcrV2?.hasModel} 
                                        className="pull-right run-generate-model-cb-parent" 
                                        onChange={this.props.onGenerateModelChange} 
                                        disabled={this.props.adHocOcrV2 && this.props.adHocOcrV2.hasRunningRequest}
                                    />
                                    {this.props.adHocOcrV2 && this.props.adHocOcrV2.hasRunningRequest  && 
                                        <ReactTooltip place="left" type="light" effect="solid">
                                            <FontIcon iconName="Info12" /> &nbsp;This feature is temporary unavailable since there is a running request for new model
                                        </ReactTooltip>
                                    }
                                </div>                                
                            }         
                            {/* <PrimaryButton ariaDescription="Generate Auto Label Model" onClick={this.props.postAdHocToSource} className="save-to-optimus" >
                                <FontIcon 
                                        iconName='Save' 
                                        
                                        title="Generate Model"                                
                                />                        
                                &nbsp;
                                Generate Model
                            </PrimaryButton> */}
                        </div>
                    </React.Fragment>
                }                
                
            </div>
        );
    }

    private addDefaultMenuItems = (menu: Electron.Menu) => {
        if (!menu) {
            return null;
        }

        return menu.items.reduce(this.renderMenuItem, []);
    };

    private showUpload = () => {
        this.setState({
            showFileUpload: true,
        });
    };

    private renderMenuItem = (results, menuItem: Electron.MenuItem) => {
        if (!menuItem.visible) {
            return null;
        }

        const itemType: string = menuItem["type"];

        switch (itemType) {
            case "separator":
                results.push({
                    key: menuItem.label,
                    itemType: ContextualMenuItemType.Divider,
                });
                break;
            case "submenu":
                results.push({
                    key: menuItem.label,
                    name: menuItem.label,
                    subMenuProps: {
                        theme: getSubMenuTheme(),
                        items: this.addDefaultMenuItems(menuItem["submenu"]),
                    },
                });
                break;
            case "normal":
                results.push({
                    key: menuItem.label,
                    name: menuItem.label,
                    onClick: (e) => this.onMenuItemClick(e, menuItem),
                });
        }
        return results;
    };

    private onMenuItemClick(e: any, menuItem: Electron.MenuItem) {
        if (menuItem.label === "Zoom In") {
            this.currentWindow.webContents.setZoomLevel(
                this.currentWindow.webContents.getZoomLevel() + 0.3
            );
        } else if (menuItem.label === "Zoom Out") {
            this.currentWindow.webContents.setZoomLevel(
                this.currentWindow.webContents.getZoomLevel() - 0.3
            );
        } else if (menuItem.label === "Reset Zoom") {
            this.currentWindow.webContents.setZoomLevel(0);
        } else if (menuItem.click) {
            menuItem.click.call(menuItem, menuItem, this.currentWindow);
        }
    }

    private onMaximize = (isMaximized: boolean) => {
        this.setState({
            maximized: isMaximized,
        });
    };

    private onFullScreen = (isFullScreen: boolean) => {
        this.setState({
            fullscreen: isFullScreen,
        });
    };

    private minimizeWindow = () => {
        this.currentWindow.minimize();
    };

    private maximizeWindow = () => {
        this.currentWindow.maximize();
    };

    private unmaximizeWindow = () => {
        this.currentWindow.unmaximize();
    };

    private closeWindow = () => {
        this.currentWindow.close();
    };
}
